import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { soccerPath, soccerUrl } from '../utils/paths';
import { mediaUrl } from '../utils/blogUtils';
import SoccerFieldVisualization from './SoccerFieldVisualization';
import './PublicPracticeFormation.css';

const sortByName = (a, b) =>
  (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' });

const STATUS_LABELS = {
  AVAILABLE: 'Available',
  RESERVE: 'Reserve',
  MAYBE: 'Maybe',
  UNAVAILABLE: 'Unavailable',
};

const formatPosition = (position) =>
  position ? position.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '';

const PublicPracticeFormation = () => {
  const { token } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const loadPage = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getPublicPracticeFormation(token);
      setPage(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load practice page');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const teamsPublished = !!page?.teamsPublished;
  const teamsDrafted = !!page?.teamsDrafted;
  const teamsFormed = teamsPublished;
  const summary = page?.availabilitySummary;
  const availability = page?.availability || [];

  const teamsForVisualization = useMemo(() => {
    if (!page || !teamsFormed || !page.divisions?.length) return null;
    return {
      id: page.practiceId,
      name: page.teamName,
      numberOfTeams: page.numberOfTeams,
      divisions: (page.divisions || []).map((division) => ({
        ...division,
        players: [...(division.players || [])].sort(sortByName),
        averageSkill: null,
      })),
      customFieldPositions: page.customFieldPositions || {},
    };
  }, [page, teamsFormed]);

  const practiceForVisualization = useMemo(() => {
    if (!page) return null;
    return {
      title: page.title,
      location: page.location,
      practiceDate: page.practiceDate,
    };
  }, [page]);

  const shareUrl = soccerUrl(`/public/practice-formation/${token}`);
  const playerProfilePath = (playerId) =>
    soccerPath(`/public/practice-formation/${token}/players/${playerId}`);

  const handleShareViaWhatsApp = () => {
    if (!page) return;
    const url = api.sharePracticeFormationViaWhatsApp({
      title: page.title,
      practiceDate: page.practiceDate,
      location: page.location,
      shareToken: token,
      clubName: page.clubName,
      teamsFormed: teamsPublished,
    });
    window.open(url, '_blank');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      window.prompt('Copy this link:', shareUrl);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderStatusBadge = (status) => {
    if (!status) {
      return <span className="public-formation__status-badge public-formation__status-badge--pending">Not voted</span>;
    }
    const key = status.toLowerCase();
    return (
      <span className={`public-formation__status-badge public-formation__status-badge--${key}`}>
        {STATUS_LABELS[status] || status}
      </span>
    );
  };

  if (loading) {
    return <div className="loading">Loading practice page…</div>;
  }

  if (error) {
    return (
      <div className="public-formation">
        <div className="card">
          <div className="alert alert-error">{error}</div>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="public-formation">
        <div className="card">
          <div className="alert alert-warning">Practice page not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-formation">
      <header className="public-formation__header">
        <p className="public-formation__club">{page.clubName}</p>
        <h1>{page.title}</h1>
        {page.description && (
          <p className="public-formation__description">{page.description}</p>
        )}
        <div className="public-formation__meta">
          {page.practiceDate && (
            <span>📅 {formatDate(page.practiceDate)}</span>
          )}
          {page.location && <span>📍 {page.location}</span>}
        </div>
        <div className="public-formation__actions">
          <button type="button" className="btn btn-whatsapp" onClick={handleShareViaWhatsApp}>
            📱 Share via WhatsApp
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleCopyLink}>
            {copied ? 'Link copied!' : 'Copy link'}
          </button>
        </div>
      </header>

      <section className="public-formation__availability">
        <h2>Player availability</h2>
        <p className="public-formation__availability-help">
          Players who voted Available or Reserve for this practice. Tap a player to view their practice record.
        </p>
        {summary && (
          <div className="public-formation__availability-summary">
            <span className="public-formation__availability-pill public-formation__availability-pill--available">
              ✅ {summary.available} available
            </span>
            <span className="public-formation__availability-pill public-formation__availability-pill--reserve">
              🪑 {summary.reserve} reserve
            </span>
          </div>
        )}
        {availability.length === 0 ? (
          <p className="public-formation__teams-pending">
            No players have voted Available or Reserve yet.
          </p>
        ) : (
          <div className="public-formation__availability-grid">
            {availability.map((player) => {
              const photoUrl = player.profilePictureUrl
                ? mediaUrl(player.profilePictureUrl)
                : null;
              return (
                <div key={player.playerId} className="public-formation__availability-item">
                  <Link
                    to={playerProfilePath(player.playerId)}
                    className="public-formation__player-link"
                  >
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt=""
                        className="public-formation__player-photo"
                      />
                    ) : (
                      <span className="public-formation__player-initial" aria-hidden="true">
                        {(player.name || '?').charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span className="public-formation__player-details">
                      <strong>{player.name}</strong>
                      {player.position && (
                        <span className="public-formation__position">
                          {formatPosition(player.position)}
                        </span>
                      )}
                    </span>
                  </Link>
                  {renderStatusBadge(player.status)}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {!teamsDrafted && (
        <div className="public-formation__teams-pending">
          Teams have not been formed yet. Check back after the club admin publishes the lineup.
        </div>
      )}

      {teamsDrafted && !teamsPublished && (
        <div className="public-formation__teams-pending">
          Teams are being finalized. Availability is shown below — the lineup will appear here once the admin publishes it.
        </div>
      )}

      {teamsFormed && page.divisions && page.divisions.length > 0 && (
        <section className="public-formation__teams">
          <h2>Teams</h2>
          <p className="public-formation__teams-help">
            Tap a player to view their club practice record and highlights.
          </p>
          <div className="public-formation__team-grid">
            {page.divisions.map((division, idx) => {
              const isRed =
                division.teamName.includes('Red') || division.teamName.includes('🔴');
              const isBlue =
                division.teamName.includes('Blue') || division.teamName.includes('🔵');
              const borderColor = isRed ? '#dc3545' : isBlue ? '#007bff' : '#6c757d';
              const bgColor = isRed ? '#fff5f5' : isBlue ? '#f0f8ff' : '#f9f9f9';
              const sortedPlayers = [...(division.players || [])].sort(sortByName);

              return (
                <div
                  key={idx}
                  className="public-formation__team-card"
                  style={{ borderColor, backgroundColor: bgColor }}
                >
                  <h3>{division.teamName}</h3>
                  {division.formation && (
                    <p className="public-formation__formation-label">
                      Formation: {division.formation}
                    </p>
                  )}
                  <ul className="public-formation__player-list">
                    {sortedPlayers.map((player) => {
                      const photoUrl = player.profilePictureUrl
                        ? mediaUrl(player.profilePictureUrl)
                        : null;
                      return (
                        <li key={player.id} className="public-formation__player-item">
                          <Link
                            to={playerProfilePath(player.id)}
                            className="public-formation__player-link"
                          >
                            {photoUrl ? (
                              <img
                                src={photoUrl}
                                alt=""
                                className="public-formation__player-photo"
                              />
                            ) : (
                              <span className="public-formation__player-initial" aria-hidden="true">
                                {(player.name || '?').charAt(0).toUpperCase()}
                              </span>
                            )}
                            <span className="public-formation__player-details">
                              <strong>{player.name}</strong>
                              {player.position && (
                                <span className="public-formation__position">
                                  {formatPosition(player.position)}
                                </span>
                              )}
                              <span className="public-formation__player-record-cta">View record →</span>
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {teamsFormed && page.divisions && page.divisions.length > 0 && (
        <section className="public-formation__field">
          <h2>Field layout</h2>
          <p className="public-formation__teams-help">
            Tap a team formation to open a larger view. Tap a player to view their practice record.
          </p>
          <SoccerFieldVisualization
            teams={teamsForVisualization}
            practice={practiceForVisualization}
            readOnly
            layout="vertical"
            showPlayerPhotos
            alwaysVisible
            initialShowField
            zoomableTeams
            getPlayerProfileLink={(player) =>
              player?.id ? playerProfilePath(player.id) : null
            }
          />
        </section>
      )}

      {page.videoLinks && page.videoLinks.length > 0 && (
        <section className="public-formation__videos">
          <h2>Practice videos</h2>
          <ul>
            {page.videoLinks.map((link) => (
              <li key={link}>
                <a href={link} target="_blank" rel="noopener noreferrer">
                  {link}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default PublicPracticeFormation;
